<template>
    <Dropdown class="SplitButton">
        <template v-slot="{toggleDropdown, open}">
            <div
                :class="[
                    'SplitButton__button-container',
                    `SplitButton__button-container--${size}`
                ]"
            >
                <!--
                Fired when an option is clicked
                @event select
                @property value The value of the selected option
            -->
                <button
                    :disabled="disabled"
                    :class="[
                        'SplitButton__button',
                        !showDropdownButton && 'SplitButton__button--single-option',
                    ]"
                    @click="$emit('select', options[0].value)"
                >
                    {{ options[0].label }}
                </button>
                <button
                    v-if="showDropdownButton"
                    :disabled="disabled"
                    class="SplitButton__select-button"
                    @click="toggleDropdown"
                >
                    <StreamlineIcon
                        fill="#FFFFFF"
                        :icon="open ? ArrowUp1: ArrowDown1"
                        :size="8"
                    />
                </button>
            </div>
        </template>
        <template v-slot:dropdown="{close}">
            <div class="SplitButton__dropdown">
                <button
                    v-for="opt in parsedOption"
                    :key="opt.label"
                    :class="[
                        'SplitButton__option',
                        iconPosition === 'right' && 'SplitButton__option--reversed',
                        showIcon(opt) &&'SplitButton__option--icon',
                    ]"
                    @click="select(opt.value, close)"
                >
                    <StreamlineIcon
                        v-if="showIcon(opt)"
                        :icon="getIcon(opt).icon"
                        :stroke="getIcon(opt).color"
                        :fill="getIcon(opt).fill"
                        :size="16"
                    />
                    <span>{{ opt.label }}</span>
                </button>
            </div>
        </template>
    </Dropdown>
</template>

<script>
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {ArrowUp1, ArrowDown1} from "@reviewpush/streamline-bold/lib/arrows-diagrams/Arrows";
import {cloneDeep, has} from 'lodash';
import stat from 'status';
import {white} from 'style/_colors.scss';
import Dropdown from '@/components/Basic/Elements/Dropdown.vue';

export default {
    name: 'RPSplitButton',

    components: {
        StreamlineIcon,
        Dropdown
    },

    props: {
        /**
         * An array of objects, each with a `label` and `value` property
         */
        options: {
            type: Array,
            required: true,
            validator: (a) => a.length > 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'lg'
        },
        iconPosition: {
            type: String,
            default: 'right'
        },
        /**
         * A status pulled from the common `status` constants (`/src/status/index.js`)
         */
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    data() {
        return {
            ArrowUp1,
            ArrowDown1,
        };
    },
    computed: {
        showDropdownButton(){
            return this.options.length > 1;
        },
        parsedOption(){
            return cloneDeep(this.options).splice(1);
        },
    },
    methods: {
        showIcon(val){
            return has(val, 'icon');
        },
        getIcon(val){
            return Object.assign(
                {
                    icon: {},
                    color: 'transparent',
                    fill: 'transparent',
                },
                val.icon
            );
        },
        select(val, close){
            close();
            this.$emit('select', val);
        },
    },
};
</script>

<style lang="scss">
.SplitButton  {
    display: table-cell;

    &__button-container{
        display: flex;
        align-items: center;
        &--xl { height: 3rem; }
        &--lg { height: 2.5rem; }
        &--md { height: 2rem; }
        &--sm { height: 1.5rem; }
    }

    &__button,
    &__select-button {
        height: 100%;
        background: $primary;
        border-color: $primary;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $white;
        &:hover { background: $primary-400; }
        &:active { background: $primary-500; }
        &:disabled {
            cursor: default;
            border-color: $primary-100;
            background: $primary-100;
        }
    }

    &__button {
        padding: 0 1rem;
        border-radius: 4px 0 0 4px;
        margin-right: 1px;
        font-weight: 600;
        white-space: nowrap;

        &:focus {
            outline:none;
            box-shadow: 0 0 4px $primary;
            border-radius: 4px 0 0 4px;
        }
        &--single-option {
            border-radius: 4px;
            &:focus {
                outline:none;
                box-shadow: 0 0 4px $primary;
                border-radius: 4px;
            }
        }
    }
    &__select-button {
        width: 2.5rem;
        border-radius: 0 4px 4px 0;
        &:focus {
            outline:none;
            box-shadow: 0 0 4px $primary;
            border-radius: 0 4px 4px 0;
        }
    }
    &__dropdown{
        background: $white;
        padding: 0.5rem;
        border: 1px solid $grey-100;
        border-radius: 4px;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);
        margin-top: 0.25rem;
        display: flex;
        flex-direction: column;
    }
    &__option {
        border-radius: 4px;
        padding: 0.25rem 0.5rem;
        display: flex;
        justify-content: space-around;
        &--icon{
            justify-content: space-between;
        }
        &--reversed {
            flex-direction: row-reverse;
        }
        &:hover { background: $grey-50; }
        &:active{
            background: $primary;
            color: $white;
        }
        :focus {
            outline: none;
            box-shadow: 0px 0px 4px $primary;
        }
        &:not(:last-child){
            margin-bottom: 0.5rem;
        }
    }
}
</style>

<docs>
```vue
<RPSplitButton
    size="xl"
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
<br>
<RPSplitButton
    size="xl"
    :disabled="true"
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
<br>
<RPSplitButton
    size="lg"
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
<br>
<RPSplitButton
    size="md"
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
<br>
<RPSplitButton
    size="md"
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
<br>
<RPSplitButton
    size="sm"
    :options="[
        {label: 'Do A Thing', value: 'option-1'},
        {label: 'Do Another Thing', value: 'option-2'}
    ]"
></RPSplitButton>
```

```vue
import { StairsAscend } from "@reviewpush/streamline-regular/lib/wayfinding/Stairs";
<p>With icons on the dropdown items (can be positioned on left or right):</p><br>

<RPSplitButton
    icon-position="left"
    :options="[
        {label: 'Don\'t Go Upstairs', value: 'dont-go-upstairs'},
        {label: 'Stay Put', value: 'stay-put'},
        {label: 'Go Upstairs', value: 'go-upstairs', icon: {icon: StairsAscend, color: '#586672'}},
    ]"
></RPSplitButton>
<br>
<RPSplitButton
    icon-position="right"
    :options="[
        {label: 'Don\'t Go Upstairs', value: 'dont-go-upstairs'},
        {label: 'Stay Put', value: 'stay-put'},
        {label: 'Go Upstairs', value: 'go-upstairs', icon: {icon: StairsAscend, color: '#586672'}},
    ]"
></RPSplitButton>
```
</docs>
